import { forwardRef, MouseEventHandler } from 'react';
import { Button, makeStyles, shorthands } from '@fluentui/react-components';
import { OpenRegular } from '@fluentui/react-icons';
import { Link } from 'react-router-dom';

import { TERMINOLOGY } from '@shared/i18n';

type TAppearance = 'default' | 'transparent';

type TProps = {
  literatureId: string;
  /**
   * `default` (bubble menu look): Fluent default + `noBorder` shorthand.
   * `transparent` (dialog row look): no rest-state background so the button
   * sits quietly inside a clickable list row until hovered.
   */
  appearance?: TAppearance;
  /**
   * Pass `(e) => e.stopPropagation()` when this button sits inside a parent
   * that has its own click handler (e.g. the citation insert dialog row).
   * Without it the row's onClick fires alongside the navigation.
   */
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};

const useStyles = makeStyles({
  noBorder: {
    ...shorthands.borderColor('transparent'),
  },
});

// Single source of truth for the "open literature in a new tab" affordance.
// Used by the citation insert dialog row and the citation bubble menu so
// both surfaces stay visually consistent. Consumers are expected to wrap
// this in a `<Tooltip>`. We use `forwardRef` + `...rest` spread on the
// outer `Link` so the Tooltip's injected pointer/focus handlers and ref
// reach the underlying `<a>` element. The inner `<Button>` gives the icon
// its own Fluent styling, shielding it from any ancestor CSS that colors
// `<a>` elements (e.g. the editor content stylesheet).
const OpenLiteratureButton = forwardRef<HTMLAnchorElement, TProps>(
  ({ literatureId, appearance = 'default', onClick, ...rest }, ref) => {
    const classes = useStyles();
    const ariaLabel = `Open ${TERMINOLOGY.literature.lower} in a new tab`;

    const isTransparent = appearance === 'transparent';

    return (
      <Link
        to={`/literature/${literatureId}`}
        target="_blank"
        rel="noopener noreferrer"
        // `unstyled` opts the anchor out of the `.ProseMirror a` blue/underline
        // rule in content.scss, which would otherwise cascade into the Button
        // icon via `currentColor` when the bubble menu is rendered inside the
        // editor content tree.
        className="unstyled"
        onClick={onClick}
        ref={ref}
        {...rest}
      >
        <Button
          icon={<OpenRegular />}
          appearance={isTransparent ? 'transparent' : undefined}
          className={isTransparent ? '!min-w-0' : classes.noBorder}
          aria-label={ariaLabel}
        />
      </Link>
    );
  }
);

OpenLiteratureButton.displayName = 'OpenLiteratureButton';

export default OpenLiteratureButton;
